import api from './api';

export interface DeliveryNeighborhood {
  id: string;
  cnpj: string;
  cityId: string;
  name: string;
  feeInCents: number;
  fee: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  cityName?: string;
  cityState?: string;
}

export interface ListDeliveryNeighborhoodsParams {
  cityId?: string;
  searchTerm?: string;
  isActive?: boolean;
  cnpj?: string;
}

export interface CreateDeliveryNeighborhoodPayload {
  cityId: string;
  name: string;
  feeInCents: number;
  isActive?: boolean;
  cnpj?: string;
}

export interface UpdateDeliveryNeighborhoodPayload {
  name?: string;
  feeInCents?: number;
  isActive?: boolean;
  cnpj?: string;
}

const normalizeNeighborhood = (data: any): DeliveryNeighborhood => ({
  id: String(data.id ?? ''),
  cnpj: String(data.cnpj ?? ''),
  cityId: String(data.cityId ?? ''),
  name: String(data.name ?? ''),
  feeInCents: Number(data.feeInCents ?? Math.round((Number(data.fee ?? 0) || 0) * 100)),
  fee: Number(data.fee ?? (Number(data.feeInCents ?? 0) || 0) / 100),
  isActive: Boolean(data.isActive ?? true),
  createdAt: String(data.createdAt ?? new Date().toISOString()),
  updatedAt: String(data.updatedAt ?? new Date().toISOString()),
  cityName: data.cityName ? String(data.cityName) : undefined,
  cityState: data.cityState ? String(data.cityState).toUpperCase() : undefined,
});

export const deliveryNeighborhoodService = {
  async list(params: ListDeliveryNeighborhoodsParams = {}): Promise<DeliveryNeighborhood[]> {
    const response = await api.get('/admin/delivery/neighborhoods', {
      params: {
        cityId: params.cityId?.trim() || undefined,
        searchTerm: params.searchTerm?.trim() || undefined,
        isActive: params.isActive,
        cnpj: params.cnpj?.trim() || undefined,
      },
    });

    const payload = Array.isArray(response.data) ? response.data : [];
    return payload.map(normalizeNeighborhood);
  },

  async get(id: string, cnpj?: string): Promise<DeliveryNeighborhood> {
    const response = await api.get(`/admin/delivery/neighborhoods/${id}`, {
      params: {
        cnpj: cnpj?.trim() || undefined,
      },
    });

    return normalizeNeighborhood(response.data);
  },

  async create(payload: CreateDeliveryNeighborhoodPayload): Promise<DeliveryNeighborhood> {
    const response = await api.post(
      '/admin/delivery/neighborhoods',
      {
        cityId: payload.cityId,
        name: payload.name.trim(),
        feeInCents: payload.feeInCents,
        isActive: payload.isActive,
      },
      {
        params: {
          cnpj: payload.cnpj?.trim() || undefined,
        },
      },
    );

    return normalizeNeighborhood(response.data);
  },

  async update(id: string, payload: UpdateDeliveryNeighborhoodPayload): Promise<DeliveryNeighborhood> {
    const response = await api.put(
      `/admin/delivery/neighborhoods/${id}`,
      {
        name: payload.name?.trim(),
        feeInCents: payload.feeInCents,
        isActive: payload.isActive,
      },
      {
        params: {
          cnpj: payload.cnpj?.trim() || undefined,
        },
      },
    );

    return normalizeNeighborhood(response.data);
  },

  async delete(id: string, cnpj?: string): Promise<void> {
    await api.delete(`/admin/delivery/neighborhoods/${id}`, {
      params: {
        cnpj: cnpj?.trim() || undefined,
      },
    });
  },
};

export default deliveryNeighborhoodService;
